import {
  ApiError,
  isRecord,
  unwrapList,
  unwrapRecord,
  type AdminApiClient,
  type JsonRecord,
} from './api-client';

export const configText = (row: JsonRecord, key: string, fallback = '') =>
  typeof row[key] === 'string' ? (row[key] as string) : fallback;
export const configRecord = (value: unknown): JsonRecord => (isRecord(value) ? value : {});
export const sameConfig = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

export type ConfigurationSection = 'base' | 'cities' | 'products' | 'service' | 'payment';
export type ProductSetting = { enabled: boolean; titleOverride: string };
export type CitySetting = { enabled: boolean; sortOrder: number; showCityNavigation?: boolean };
export type ConfigurationDraft = {
  base: { displayName: string; wechatAppIdRef: string; status: string };
  home: { defaultCityId: string; defaultOrderMode: string; primaryColor: string };
  cities: Record<string, CitySetting>;
  products: Record<string, ProductSetting>;
  service: JsonRecord;
  merchantIds: string[];
};
export type ConfigurationData = {
  app: JsonRecord;
  runtime: JsonRecord;
  cities: JsonRecord[];
  cityLinks: JsonRecord[];
  products: JsonRecord[];
  merchants: JsonRecord[];
  bindings: JsonRecord[];
  routes: JsonRecord[];
  paymentError?: string;
  routeError?: string;
};

export async function readAllConfigurationProducts(api: AdminApiClient, appId: string) {
  const products: JsonRecord[] = [];
  let page = 1;
  let totalPages: number;
  do {
    const payload = await api.get<unknown>(
      `/api/admin/m2/products?appId=${encodeURIComponent(appId)}&includeUnavailableCities=true&page=${page}&pageSize=100`,
    );
    const result = unwrapRecord(payload);
    products.push(...unwrapList(payload));
    totalPages = Number(result.totalPages ?? 1);
    if (!Number.isInteger(totalPages) || totalPages < 0 || totalPages > 10000)
      throw new Error('产品分页信息异常，请重新加载');
    page += 1;
  } while (page <= totalPages);
  return products;
}

export async function readConfiguration(
  api: AdminApiClient,
  appId: string,
): Promise<ConfigurationData> {
  const id = encodeURIComponent(appId);
  const results = await Promise.allSettled([
    api.get<unknown>(`/api/admin/m2/apps/${id}`),
    api.get<unknown>('/api/admin/m2/cities'),
    readAllConfigurationProducts(api, appId),
    api.get<unknown>(`/api/admin/m3/apps/${id}/payment-merchant-options`),
    api.get<unknown>(`/api/admin/m3/apps/${id}/payment-bindings`),
    api.get<unknown>(`/api/admin/m3/apps/${id}/payment-route-rules`),
  ]);
  for (const index of [0, 1, 2]) {
    const result = results[index];
    if (result?.status === 'rejected') throw result.reason;
  }
  const value = (index: number) => {
    const result = results[index];
    return result?.status === 'fulfilled' ? result.value : undefined;
  };
  const detail = unwrapRecord(value(0));
  const paymentFailure = results.slice(3, 5).some((result) => result.status === 'rejected');
  return {
    app: isRecord(detail.app) ? detail.app : detail,
    runtime: configRecord(detail.runtime),
    cities: unwrapList(value(1)),
    cityLinks: unwrapList(detail.cities),
    products: value(2) as JsonRecord[],
    merchants: unwrapList(value(3)),
    bindings: unwrapList(value(4)),
    routes: unwrapList(value(5)),
    ...(paymentFailure ? { paymentError: '收款商户读取失败，其他配置仍可维护。' } : {}),
    ...(results[5]?.status === 'rejected'
      ? { routeError: '收款调度状态暂未读取，不能确认商户是否参与收款。' }
      : {}),
  };
}

export function configurationDraft(data: ConfigurationData): ConfigurationDraft {
  const service = configRecord(data.runtime.customerServiceConfig);
  const links = new Map(
    data.cityLinks.map((link) => [
      configText(link, 'cityId') || configText(configRecord(link.city), 'id'),
      link,
    ]),
  );
  return {
    base: {
      displayName: configText(data.app, 'displayName'),
      wechatAppIdRef: configText(data.app, 'wechatAppIdRef'),
      status: configText(data.app, 'status', 'ACTIVE'),
    },
    home: {
      defaultCityId: configText(data.runtime, 'defaultCityId'),
      defaultOrderMode: configText(data.runtime, 'defaultOrderMode', 'STANDARD'),
      primaryColor: configText(configRecord(data.runtime.themeConfig), 'primaryColor', '#16a085'),
    },
    cities: Object.fromEntries(
      data.cities.map((city) => {
        const link = links.get(configText(city, 'id'));
        return [
          configText(city, 'id'),
          {
            enabled: link?.enabled === true,
            sortOrder: Number(link?.sortOrder ?? 0),
            ...(link?.showCityNavigation === false ? { showCityNavigation: false } : {}),
          },
        ];
      }),
    ),
    products: Object.fromEntries(
      data.products.map((product) => {
        const override = configRecord(product.appOverride);
        return [
          configText(product, 'id'),
          {
            enabled: override.enabled === true,
            titleOverride: configText(override, 'titleOverride'),
          },
        ];
      }),
    ),
    service: { ...service, enabled: service.enabled === true },
    merchantIds: data.bindings
      .filter((binding) => binding.status !== 'DISABLED')
      .map((binding) => configText(binding, 'merchantId'))
      .sort(),
  };
}

export type ConfigurationIssue = { section: ConfigurationSection; field: string; message: string };
export function validateConfiguration(
  draft: ConfigurationDraft,
  data: ConfigurationData,
  saved = configurationDraft(data),
): ConfigurationIssue[] {
  const issues: ConfigurationIssue[] = [];
  const add = (section: ConfigurationSection, field: string, message: string) =>
    issues.push({ section, field, message });
  if (!draft.base.displayName.trim()) add('base', 'displayName', '请输入小程序名称');
  if (!/^#[0-9a-f]{6}$/i.test(draft.home.primaryColor))
    add('base', 'primaryColor', '品牌色须为六位色值，例如 #16A085');
  const activeCities = data.cities.filter(
    (city) => city.status === 'ACTIVE' && draft.cities[configText(city, 'id')]?.enabled,
  );
  if (!activeCities.some((city) => city.id === draft.home.defaultCityId))
    add('cities', 'defaultCityId', '请从已开放城市中选择首页默认城市');
  for (const [id, setting] of Object.entries(draft.cities)) {
    if (!Number.isInteger(setting.sortOrder) || setting.sortOrder < 0 || setting.sortOrder > 99999)
      add('cities', `city-${id}`, '城市排序请输入 0–99999 的整数');
  }
  for (const product of data.products) {
    const id = configText(product, 'id');
    if (sameConfig(saved.products[id], draft.products[id])) continue;
    const cityId = configText(product, 'cityId');
    if (!activeCities.some((city) => city.id === cityId)) {
      add(
        'products',
        `product-${id}`,
        `「${configText(product, 'title')}」有未保存修改，请先开放所属城市或撤销该产品修改`,
      );
    }
  }
  if (draft.service.enabled === true) {
    if (!configText(draft.service, 'displayName').trim())
      add('service', 'serviceName', '请输入客服名称');
    if (!configText(draft.service, 'serviceHours').trim())
      add('service', 'serviceHours', '请输入服务时间');
    if (
      !['servicePhone', 'serviceWechat', 'supportPagePath', 'qrCodeUrl'].some((key) =>
        configText(draft.service, key).trim(),
      )
    )
      add('service', 'servicePhone', '请至少配置一种联系方式');
    const qrCodeUrl = configText(draft.service, 'qrCodeUrl').trim();
    if (qrCodeUrl && !/^https:\/\/[^\s]+$/i.test(qrCodeUrl))
      add('service', 'qrCodeUrl', '请输入完整的 HTTPS 地址');
    const corpId = configText(draft.service, 'serviceWechat').trim();
    const supportUrl = configText(draft.service, 'supportPagePath').trim();
    if (
      supportUrl &&
      !/^https:\/\/work\.weixin\.qq\.com\/kfid\/[A-Za-z0-9_-]+(?:[/?#][^\s]*)?$/u.test(supportUrl)
    ) {
      add(
        'service',
        'supportPagePath',
        '请填写企业微信后台提供的微信客服链接：https://work.weixin.qq.com/kfid/…',
      );
    }
    if (supportUrl && !corpId)
      add('service', 'serviceWechat', '使用微信客服链接时，请同时填写企业 ID（CorpID）');
    if (corpId && !/^[A-Za-z0-9_-]+$/u.test(corpId))
      add(
        'service',
        'serviceWechat',
        '请复制企业微信后台的企业 ID（CorpID），勿填写个人微信号或企业名称',
      );
    if (corpId && !supportUrl)
      add('service', 'supportPagePath', '已填写企业 ID，请同时配置该企业的微信客服链接');
  }
  return issues;
}

export type ConfigurationChange = {
  id: string;
  section: ConfigurationSection;
  label: string;
  path: string;
  method: 'PUT' | 'PATCH';
  body: JsonRecord;
  apply: (previous: ConfigurationDraft) => ConfigurationDraft;
};

function normalizedService(service: JsonRecord): JsonRecord {
  const next = { ...service };
  for (const key of ['servicePhone', 'serviceWechat', 'supportPagePath', 'qrCodeUrl']) {
    if (typeof next[key] === 'string') next[key] = next[key].trim();
  }
  return next;
}

export function configurationChanges(
  saved: ConfigurationDraft,
  draft: ConfigurationDraft,
  data: ConfigurationData,
): ConfigurationChange[] {
  const changes: ConfigurationChange[] = [];
  const appId = encodeURIComponent(configText(data.app, 'id'));
  const cityChange = (id: string, value: CitySetting) => ({
    id: `city-${id}`,
    section: 'cities' as const,
    label: `${value.enabled ? '开放' : '关闭'}城市 · ${configText(data.cities.find((city) => city.id === id) ?? {}, 'name')}`,
    path: `/api/admin/m2/apps/${appId}/cities/${encodeURIComponent(id)}`,
    method: 'PUT' as const,
    body: value,
    apply: (previous: ConfigurationDraft) => ({
      ...previous,
      cities: { ...previous.cities, [id]: value },
    }),
  });
  const changedCities = Object.entries(draft.cities).filter(
    ([id, value]) =>
      !sameConfig(
        { ...value, showCityNavigation: value.showCityNavigation !== false },
        { ...saved.cities[id], showCityNavigation: saved.cities[id]?.showCityNavigation !== false },
      ),
  );
  // Make the new default available before changing it; close the old default only afterwards.
  for (const [id, value] of changedCities.filter(([, value]) => value.enabled))
    changes.push(cityChange(id, value));
  const homeChanged = !sameConfig(saved.home, draft.home);
  const serviceChanged = !sameConfig(saved.service, draft.service);
  if (homeChanged || serviceChanged)
    changes.push({
      id: 'runtime',
      section:
        saved.home.defaultCityId !== draft.home.defaultCityId
          ? 'cities'
          : homeChanged
            ? 'base'
            : 'service',
      label:
        homeChanged && serviceChanged ? '首页规则与客服' : homeChanged ? '首页规则' : '客服入口',
      path: `/api/admin/m2/apps/${appId}/runtime`,
      method: 'PUT',
      body: {
        ...(homeChanged
          ? {
              defaultCityId: draft.home.defaultCityId,
              defaultOrderMode: draft.home.defaultOrderMode,
              themeConfig: {
                ...configRecord(data.runtime.themeConfig),
                primaryColor: draft.home.primaryColor,
              },
            }
          : {}),
        ...(serviceChanged ? { customerServiceConfig: normalizedService(draft.service) } : {}),
      },
      apply: (previous) => ({
        ...previous,
        ...(homeChanged ? { home: { ...draft.home } } : {}),
        ...(serviceChanged ? { service: { ...draft.service } } : {}),
      }),
    });
  for (const [id, value] of Object.entries(draft.products)) {
    if (sameConfig(value, saved.products[id])) continue;
    changes.push({
      id: `product-${id}`,
      section: 'products',
      label: `产品 · ${configText(data.products.find((product) => product.id === id) ?? {}, 'title')}`,
      path: `/api/admin/m2/apps/${appId}/products/${encodeURIComponent(id)}/override`,
      method: 'PUT',
      body: { enabled: value.enabled, titleOverride: value.titleOverride.trim() || null },
      apply: (previous) => ({ ...previous, products: { ...previous.products, [id]: value } }),
    });
  }
  for (const [id, value] of changedCities.filter(([, value]) => !value.enabled))
    changes.push(cityChange(id, value));
  if (!sameConfig([...saved.merchantIds].sort(), [...draft.merchantIds].sort()))
    changes.push({
      id: 'payment',
      section: 'payment',
      label: '收款商户关联',
      path: `/api/admin/m3/apps/${appId}/payment-bindings`,
      method: 'PUT',
      body: { merchantIds: draft.merchantIds },
      apply: (previous) => ({ ...previous, merchantIds: [...draft.merchantIds] }),
    });
  // Status is last: disabling never strands the remaining maintenance requests.
  if (!sameConfig(saved.base, draft.base))
    changes.push({
      id: 'base',
      section: 'base',
      label: '基础资料与运营状态',
      path: `/api/admin/m2/apps/${appId}`,
      method: 'PATCH',
      body: {
        ...draft.base,
        displayName: draft.base.displayName.trim(),
        wechatAppIdRef: draft.base.wechatAppIdRef.trim() || null,
      },
      apply: (previous) => ({ ...previous, base: { ...draft.base } }),
    });
  return changes;
}

function comparableConfiguration(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(comparableConfiguration);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, comparableConfiguration(value[key])]),
  );
}

function normalizedConfiguration(draft: ConfigurationDraft): ConfigurationDraft {
  return {
    ...draft,
    cities: Object.fromEntries(
      Object.entries(draft.cities).map(([id, city]) => [
        id,
        { ...city, showCityNavigation: city.showCityNavigation !== false },
      ]),
    ),
    base: {
      ...draft.base,
      displayName: draft.base.displayName.trim(),
      wechatAppIdRef: draft.base.wechatAppIdRef.trim(),
    },
    products: Object.fromEntries(
      Object.entries(draft.products).map(([id, product]) => [
        id,
        { ...product, titleOverride: product.titleOverride.trim() },
      ]),
    ),
    service: normalizedService(draft.service),
    merchantIds: [...new Set(draft.merchantIds)].sort(),
  };
}

/** Reconcile uncertain responses without repeating completed writes or overwriting newer data. */
export function recoverConfigurationChanges(
  saved: ConfigurationDraft,
  draft: ConfigurationDraft,
  currentData: ConfigurationData,
): {
  confirmed: ConfigurationChange[];
  pending: ConfigurationChange[];
  conflict?: ConfigurationChange;
} {
  const current = normalizedConfiguration(configurationDraft(currentData));
  const baseline = normalizedConfiguration(saved);
  const goal = normalizedConfiguration(draft);
  const changes = configurationChanges(saved, draft, currentData);
  let confirmedState = saved;
  let conflict: ConfigurationChange | undefined;
  const equals = (left: unknown, right: unknown) =>
    sameConfig(comparableConfiguration(left), comparableConfiguration(right));
  function check(
    change: ConfigurationChange,
    before: unknown,
    expected: unknown,
    actual: unknown,
    confirm: () => void,
  ) {
    if (equals(actual, expected)) confirm();
    else if (!equals(actual, before)) conflict ??= change;
  }
  for (const change of changes) {
    if (change.id === 'runtime') {
      if (!sameConfig(saved.home, draft.home))
        check(change, baseline.home, goal.home, current.home, () => {
          confirmedState = { ...confirmedState, home: { ...draft.home } };
        });
      if (!sameConfig(saved.service, draft.service))
        check(change, baseline.service, goal.service, current.service, () => {
          confirmedState = { ...confirmedState, service: { ...draft.service } };
        });
    } else if (change.id === 'base') {
      check(change, baseline.base, goal.base, current.base, () => {
        confirmedState = change.apply(confirmedState);
      });
    } else if (change.id === 'payment') {
      if (currentData.paymentError) conflict ??= change;
      else
        check(change, baseline.merchantIds, goal.merchantIds, current.merchantIds, () => {
          confirmedState = change.apply(confirmedState);
        });
    } else if (change.id.startsWith('city-')) {
      const id = change.id.slice(5);
      check(change, baseline.cities[id], goal.cities[id], current.cities[id], () => {
        confirmedState = change.apply(confirmedState);
      });
    } else if (change.id.startsWith('product-')) {
      const id = change.id.slice(8);
      check(change, baseline.products[id], goal.products[id], current.products[id], () => {
        confirmedState = change.apply(confirmedState);
      });
    }
  }
  return {
    confirmed: configurationChanges(saved, confirmedState, currentData),
    pending: configurationChanges(confirmedState, draft, currentData),
    ...(conflict ? { conflict } : {}),
  };
}

export function productExposure(
  product: JsonRecord,
  draft: ConfigurationDraft,
  data: ConfigurationData,
) {
  const cityId = configText(product, 'cityId');
  if (!draft.cities[cityId]?.enabled) return '城市未开放';
  if (data.cities.find((city) => city.id === cityId)?.status !== 'ACTIVE') return '城市已停用';
  if (product.status !== 'ACTIVE') return product.status === 'DRAFT' ? '产品草稿' : '产品已停用';
  if (!draft.products[configText(product, 'id')]?.enabled) return '不展示';
  if (draft.base.status !== 'ACTIVE') return '小程序已停用';
  return '已设为展示';
}

export function configurationError(error: unknown) {
  return error instanceof ApiError || error instanceof Error ? error.message : '保存未完成，请重试';
}
